/**
 * Dr. Mani's Magic Quiz — questions.
 * Derived from dr-mm-files/dmm-quiz-spec.json -> quiz.questions, with app-specific adaptations.
 * 9 questions, with branching + conditional display + sets side-effects.
 */
import type { QuizQuestion } from './types';

export const DMM_QUESTIONS: QuizQuestion[] = [
  {
    id: 'q1_living',
    order: 1,
    type: 'single_select',
    branch: true,
    auto_advance: true,
    writes_field: 'dmm_quiz_living',
    // Why this matters: the first screen makes the time and payoff contract clear before asking for an answer.
    kicker: 'First, the lay of the land',
    lede: "Let's read your garden.",
    contract:
      '9 quick questions, about 2 minutes. At the end: your gardener type, the one thing your plants have been missing, and your plan.',
    question: 'Where do you grow?',
    options: [
      {
        value: 'apartment',
        label: 'An apartment or condo',
        sublabel: 'No yard of my own.',
        icon: 'building_balcony',
        sets: { has_lawn: false, primary_setup: 'containers' },
        branch: 'skips lawn screens; leans houseplant + container',
      },
      {
        value: 'home_lawn',
        label: 'A home, and the lawn is mine',
        sublabel: 'Front, back, the whole yard.',
        icon: 'house_grass',
        sets: { has_lawn: true },
        branch: 'unlocks lawn-size screen + lawn pains',
      },
      {
        value: 'home_little_lawn',
        label: 'A home, but little lawn',
        sublabel: 'Mostly beds, pots, and patio.',
        icon: 'house_potted',
        sets: { has_lawn: false },
        branch: 'no lawn-size screen',
      },
      {
        value: 'land',
        label: 'Land or a homestead',
        sublabel: 'Acreage, an orchard, the big project.',
        icon: 'barn_fence',
        sets: { has_lawn: true, scale: 'acre' },
        branch: 'acre-scale sizing',
      },
    ],
  },
  {
    id: 'q2_grows',
    order: 2,
    type: 'multi_select',
    writes_field: 'dmm_quiz_grows',
    kicker: 'Now the fun part',
    question: 'What do you grow?',
    instruction: 'Pick all that fit',
    options: [
      { value: 'vegetables', label: 'Vegetables', icon: 'tomato_vine', archetype_lean: 'The Provider', segment: 'vegetables' },
      { value: 'herbs', label: 'Herbs', icon: 'herb_sprig', archetype_lean: 'The Provider', segment: 'vegetables' },
      { value: 'flowers', label: 'Flowers and ornamentals', icon: 'open_bloom', archetype_lean: 'The Color Bringer', segment: 'flowers' },
      { value: 'houseplants', label: 'Houseplants', icon: 'plant_pot', archetype_lean: 'The Plant Parent', segment: 'houseplants' },
      { value: 'fruit_trees', label: 'Fruit trees or patio fruit', icon: 'fruit_tree', archetype_lean: 'The Orchard Keeper', segment: 'fruit_trees' },
      { value: 'berries_grapes', label: 'Berries, grapes and vines', icon: 'grape_cluster', archetype_lean: 'The Orchard Keeper', segment: 'fruit_trees' },
      { value: 'trees_shrubs', label: 'Trees and shrubs in the ground', icon: 'simple_tree', archetype_lean: 'The Orchard Keeper', segment: 'everything' },
      { value: 'succulents', label: 'Succulents, cactus, tropicals', icon: 'cactus_pot', archetype_lean: 'The Curator', segment: 'everything' },
      { value: 'pollinator', label: 'A pollinator or native patch', icon: 'butterfly_flower', archetype_lean: 'The Color Bringer', segment: 'flowers' },
      { value: 'just_starting', label: 'Just getting started', icon: 'seedling', archetype_lean: 'The Brave Beginner' },
      { value: 'lawn', label: 'A lawn', icon: 'grass_blades', archetype_lean: 'The Groundskeeper', segment: 'lawn', conditional: 'show only if has_lawn' },
    ],
  },
  {
    id: 'q3_setup',
    order: 3,
    type: 'multi_select',
    writes_field: 'dmm_quiz_setup',
    kicker: 'How is it planted?',
    question: 'How do you garden?',
    instruction: 'Pick all that fit',
    note: "Encodes indoor vs outdoor (replaces a separate where-they-live question).",
    options: [
      { value: 'in_ground', label: 'In the ground outdoors', icon: 'trowel_soil', environment: 'outdoor' },
      { value: 'raised_beds', label: 'Raised beds', icon: 'raised_bed', environment: 'outdoor', adds: ['coco_coir', 'rice_hulls', 'biochar'] },
      { value: 'pots_outside', label: 'Pots and containers outside', icon: 'two_pots', environment: 'outdoor', adds: ['super_soil', 'rice_hulls'] },
      { value: 'houseplants_indoor', label: 'Houseplants in pots indoors', icon: 'windowsill_pot', environment: 'indoor', adds: ['super_soil', 'rice_hulls'] },
      { value: 'greenhouse', label: 'A greenhouse', icon: 'greenhouse', environment: 'outdoor' },
    ],
  },
  {
    id: 'q4_lawn_size',
    order: 4,
    type: 'single_select',
    auto_advance: true,
    writes_field: 'dmm_quiz_lawn_size',
    conditional: 'show only if has_lawn == true',
    kicker: 'No measuring tape, just picture it',
    question: 'How big is your lawn?',
    options: [
      { value: 'small', label: 'Small', sublabel: 'A parking space or two.', icon: 'grass_patch_1', psb: '32 oz / month' },
      { value: 'tidy_lot', label: 'A tidy city lot', sublabel: 'About a tennis court.', icon: 'grass_patch_2', psb: '32 oz / month' },
      { value: 'suburban', label: 'A full suburban yard', sublabel: 'A basketball court, front and back.', icon: 'grass_patch_3', psb: '32 oz / month' },
      { value: 'big', label: 'Big', sublabel: 'A quarter acre. A couple courts.', icon: 'grass_patch_4', psb: '32 oz to 1 gal / month' },
      { value: 'large', label: 'Large', sublabel: 'An acre. Most of a football field.', icon: 'grass_patch_5', psb: '1 gallon / month' },
    ],
  },
  {
    id: 'q5_size',
    order: 5,
    type: 'single_select',
    auto_advance: true,
    writes_field: 'dmm_quiz_size',
    kicker: 'So we get your amount right',
    question: 'How much are you growing?',
    options: [
      { value: 'few', label: 'Just a few plants or pots', sublabel: 'A windowsill, a balcony, a small start.', icon: 'one_pot', ckaa: '2 lb bag, lasts months', super_soil: 'by pot count' },
      { value: 'dozen', label: 'A dozen or so', sublabel: 'A real collection, a few beds.', icon: 'three_pots', ckaa: '2 lb monthly, or 10 lb every few months' },
      { value: 'full_yard', label: 'A full garden or yard', sublabel: 'Lots of plants, big beds, real trees.', icon: 'house_garden_rows', ckaa: '10 lb monthly in season' },
      { value: 'big_property', label: 'A big property', sublabel: 'An orchard, a homestead, the works.', icon: 'barn_rows_tree', ckaa: '10 lb on subscription', flags: ['market_grower_possible'] },
    ],
  },
  {
    id: 'q6_why',
    order: 6,
    type: 'single_select',
    auto_advance: true,
    writes_field: 'dmm_quiz_why',
    kicker: 'Be honest. There is no wrong answer.',
    question: 'What are you really growing for?',
    note: "Five distinct drivers (consolidated from eight). Drives the Key Insight on the reveal. 'natural' also triggers The Purist archetype override.",
    options: [
      { value: 'grow_food', label: 'To grow my own food', icon: 'harvest_basket', why_tag: 'Grow-food' },
      { value: 'beautiful', label: 'To make it beautiful', icon: 'flower_vase', why_tag: 'Beauty' },
      { value: 'green_thumb', label: 'To be the one with the green thumb', icon: 'thumb_leaf', why_tag: 'Mastery' },
      { value: 'peace', label: 'For the peace it gives me', icon: 'leaf_still_water', why_tag: 'Peace' },
      { value: 'natural', label: 'To do it the natural way', icon: 'leaf_shield', why_tag: 'Natural', archetype_override: 'The Purist' },
    ],
  },
  {
    id: 'q7_struggle',
    order: 7,
    type: 'multi_select',
    writes_field: 'dmm_quiz_struggle',
    kicker: 'Get it off your chest. Check everything.',
    question: 'What has been driving you crazy?',
    instruction: 'Pick all that fit',
    note: 'One pain screen, spanning diagnosis signal and proof points. Everything checked is read back on the reveal.',
    options: [
      { value: 'rot_soggy', label: 'Roots rot, soil stays soggy', icon: 'droplet_root', need: 'HOME', villain_key: 'rot_soggy' },
      { value: 'yellow_pale', label: 'Yellow, pale, weak leaves', icon: 'drooping_leaf', need: 'FOOD', villain_key: 'yellow_pale' },
      { value: 'no_bloom_fruit', label: "Won't bloom or won't fruit", icon: 'closed_bud', need: 'FOOD', villain_key: 'no_bloom_fruit' },
      { value: 'plants_die', label: 'Plants just die on me', icon: 'wilted_pot', need: 'LIFE', villain_key: 'plants_die' },
      { value: 'bugs_flies', label: 'Bugs or little flies winning', icon: 'beetle', need: 'PEST', villain_key: 'bugs' },
      { value: 'smell', label: 'The fertilizer smells awful', icon: 'smell_bag', need: 'PROOF', villain_key: 'smell' },
      { value: 'chemicals', label: 'Scared of the chemicals', icon: 'no_chemicals', need: 'PROOF', villain_key: 'chemicals' },
      { value: 'ugly_mix', label: 'Ugly mix, algae, dries to a brick', icon: 'cracked_soil', need: 'HOME', villain_key: 'ugly_mix' },
      {
        value: 'no_directions',
        label: 'Nobody tells me what to actually do',
        icon: 'book_leaf',
        need: 'PROOF',
        villain_key: 'no_directions',
      },
      { value: 'lawn_rings', label: 'Brown rings or thin grass', icon: 'grass_bare_ring', need: 'LIFE', villain_key: 'lawn_rings', conditional: 'show only if has_lawn' },
    ],
  },
  {
    id: 'q8_experience',
    order: 8,
    type: 'single_select',
    auto_advance: true,
    writes_field: 'dmm_quiz_experience',
    kicker: 'No wrong answer',
    question: 'Where are you as a gardener?',
    options: [
      { value: 'new', label: 'Brand new and hopeful', icon: 'growth_1' },
      { value: 'getting_hang', label: 'Getting the hang of it', icon: 'growth_2' },
      { value: 'experienced', label: 'Experienced', icon: 'growth_3' },
      { value: 'expert', label: 'The one friends ask', icon: 'growth_4' },
    ],
  },
  {
    id: 'q9_proudest',
    order: 9,
    type: 'single_select',
    auto_advance: true,
    writes_field: 'dmm_quiz_proudest',
    kicker: 'The last one, and the realest',
    question: 'When it all comes in, what will it mean to you?',
    options: [
      { value: 'hard_thing', label: 'Proof I can do something hard', icon: 'mountain_flag' },
      { value: 'home_alive', label: 'A home that feels alive', icon: 'house_vine' },
      { value: 'share', label: 'Something to share', icon: 'basket_out' },
      { value: 'the_one', label: 'Being the one who knows how', icon: 'hand_sprout' },
      { value: 'peace', label: 'Peace and quiet in the dirt', icon: 'leaf_still_water' },
      { value: 'clean_food', label: 'Clean food for my family', icon: 'plate_leaf' },
    ],
  },
];

/** Email gate config (spec: quiz.email_gate). Captures email + zip after Q9. */
export const DMM_EMAIL_GATE = {
  order: 10,
  showsPreview: true,
  preview: 'You are {{archetype_name}}  (rest of reading blurred below the line)',
  cta: 'Show My Full Reading',
  microcopy: 'No spam. Just dirt, done right.',
  zipPlaceholder: 'Zip code (for your frost dates)',
} as const;
